import "styles/components/calendar.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import { DayPicker } from "react-day-picker";

import { buttonVariants } from "components/ui/button";
import { cn } from "lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-2", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-between pt-1 relative items-center",
        caption_label: "space-x-2 text-base font-medium",
        nav: "space-x-4 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: " rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2 rounded-md overflow-hidden",
        cell: "font-semibold  h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-range-start)]:rounded-l-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-gray-200 dark:[&:has([aria-selected])]:bg-gray-800 focus-within:relative focus-within:z-20 ",
        day: " h-9 w-9 p-0  aria-selected:opacity-100 overflow-hidden rounded-md hover:cursor-pointer dark:hover:bg-indigo-500 hover:bg-yellow-400 ",
        day_range_start: "day-range-start dark:bg-indigo-500 bg-yellow-400",
        day_range_end: "day-range-end",
        day_selected:
          "dark:bg-indigo-500 bg-yellow-400 rounded-md text-primary-foreground hover:bg-yellow-400 dark:hover:bg-indigo-500  focus:bg-yellow-400 dark:focus:bg-indigo-500",
        day_today: "dark:bg-gray-600 bg-gray-200 text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "dark:aria-selected:bg-gray-800 aria-selected:bg-gray-200 range-middle",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
